import { useRef, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { isValidEmail, isValidPhone } from "../utils/validation";
import api from "../services/api";

const useAdminOrderFormData = () => {
  const [step, setStep] = useState(0);
  const [uploadSessionId] = useState(() => uuidv4());

  const formDataCache = useRef({
    flavors: [],
    sizes: [],
    standardCakes: [],
    cheesecakes: [],
    grazeBoxes: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stdRes, cheesecakeRes, grazeBoxRes] = await Promise.all([
          api.get("/standard-cakes"),
          api.get("/cheesecakes"),
          api.get("/graze-boxes"),
        ]);
        formDataCache.current.standardCakes = stdRes.data || [];
        formDataCache.current.cheesecakes = cheesecakeRes.data || [];
        formDataCache.current.grazeBoxes = grazeBoxRes.data || [];
      } catch (err) {
        console.error("Failed to load cake data", err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [flavorsRes, sizesRes] = await Promise.all([
          api.get("/flavors"),
          api.get("/sizes"),
        ]);

        formDataCache.current.flavors = flavorsRes.data || [];
        formDataCache.current.sizes = sizesRes.data || [];
      } catch (err) {
        console.error("❌ Failed to load flavors/sizes:", err);
      }
    };

    fetchMetadata();
  }, []);

  const [formData, setFormData] = useState({
    customer: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    fulfillment: {
      method: "pickup",
      date: null,
      time: "",
      location: "",
    },
    items: [],
    payment: {
      subtotal: 0,
      tax: 0,
      deliveryFee: 0,
      total: 0,
      totalDiscount: 0,
      currency: "CAD",
      paid: false,
      paymentStatus: "pending",
      refundStatus: "not_requested",
      receiptNumber: "",
      paymentMethod: {
        type: "",
        brand: "",
        last4: "",
      },
      stripePaymentIntent: "",
      stripeReceiptUrl: "",
      stripeCustomerId: "",
      stripeChargeId: "",
    },

    meta: {
      notes: "",
      source: "admin-manual",
      uploadSessionId,
    },
  });

  const resetFormData = () => {
    setFormData({
      customer: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      },
      fulfillment: {
        method: "pickup",
        date: "",
        time: "",
        location: "",
      },
      items: [],
      payment: {
        subtotal: 0,
        tax: 0,
        total: 0,
        deliveryFee: 0,
        totalDiscount: 0,
        paid: false,
        paymentStatus: "pending",
        refundStatus: "not_requested",
        currency: "CAD",
        receiptNumber: "",
        paymentMethod: {},
      },
      meta: {
        uploadSessionId: null,
        orderId: null,
      },
      discount: {
        amount: 0,
        name: null,
        type: null,
      },
    });
  };

  const [formErrors, setFormErrors] = useState({});

  // Stepper
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 0));
  const goToStep = (index) => setStep(index);

  // Item management
  const addItem = () => {
    const newItem = {
      _id: uuidv4(),
      type: "custom", // ✅ for frontend only
      category: "custom", // ✅ will be corrected in updateItem
      subcategory: null, // ✅ always present
      description: "",
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
      customizations: [],
      image: "",
      referenceImages: [],
      note: "",
    };
    setFormData((prev) => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const updateItem = (id, updatedFields) => {
    setFormData((prev) => {
      const updatedItems = prev.items.map((item) => {
        if (item._id !== id) return item;

        let updated = { ...item };

        // Handle type change
        if ("type" in updatedFields) {
          updated = {
            _id: item._id,
            type: updatedFields.type,
            category: updatedFields.type,
            subcategory: null,
            name: "",
            displayName: "",
            productId: null,
            image: "",
            referenceImages: [],
            flavor: undefined,
            size: undefined,
            quantity: 1,
            unitPrice: 0,
            totalPrice: 0,
            customizations: [],
            note: "",
            customDescriptor: "", // for specialtyCake only; will be removed before submit
          };
        }

        if ("productId" in updatedFields && updated.type === "grazeBox") {
          const product = findGrazeBoxById(updatedFields.productId);

          if (product) {
            updated.productId = product._id;
            updated.name = product.name;
            updated.displayName = product.displayName;
            updated.image = product.images?.primary || "";
            updated.selectedSize = undefined;
            updated.selectedDiet = undefined;
            updated.giftPackaging = false;
            updated.unitPrice = 0;
            updated.totalPrice = 0;
            updated.customizations = [];
          }
        }

        // Normalize flavor
        if ("flavor" in updatedFields) {
          const f = updatedFields.flavor;

          let flavorObj =
            typeof f === "string"
              ? getFlavorById(f) || getFlavorByName(f)
              : typeof f === "object" && f !== null
              ? f
              : null;

          if (flavorObj) {
            updated.flavor = {
              _id: flavorObj._id || null,
              name: flavorObj.name || "", // <-- internal name
              displayName: flavorObj.displayName || flavorObj.name || "", // <-- human-friendly
              image: flavorObj.images?.primary || "", // optional, helps later
            };
          }
        }

        // Normalize size
        if ("size" in updatedFields) {
          const s = updatedFields.size;

          let sizeObj =
            typeof s === "string"
              ? getSizeById(s) || getSizeByName(s)
              : typeof s === "object" && s !== null
              ? s
              : null;

          if (sizeObj) {
            updated.size = {
              _id: sizeObj._id || null,
              name: sizeObj.displayName || sizeObj.name || "",
              tierCount: sizeObj.tierCount ?? null,
            };
          }
        }

        if ("image" in updatedFields) {
          updated.image = updatedFields.image;
        }

        if ("referenceImages" in updatedFields) {
          updated.referenceImages = Array.isArray(updatedFields.referenceImages)
            ? [...updatedFields.referenceImages]
            : [];
        }

        // Apply other simple field updates (like quantity, unitPrice)
        updated = {
          ...updated,
          ...[
            "quantity",
            "unitPrice",
            "customDescriptor",
            "displayName",
            "name",
            "note",
            "selectedSize",
            "selectedDiet",
            "giftPackaging",
          ].reduce((acc, key) => {
            if (key in updatedFields) acc[key] = updatedFields[key];
            return acc;
          }, {}),
        };

        // Product matching
        const flavorName = updated.flavor?.name;
        const sizeName = updated.size?.name;

        const getProduct = () => {
          if (updated.type === "standardCake") {
            return findStandardCakeByFlavor(flavorName);
          }
          // if (updated.type === "cheesecake") {
          //   return findCheesecakeByFlavor(flavorName);
          // }
          if (updated.type === "grazeBox") {
            updated.category = "grazeBox";

            // Only set name/displayName if they aren't already defined
            if (!updated.name)
              updated.name = updated.displayName || "graze_box";
            if (!updated.displayName)
              updated.displayName = updated.name || "Graze Box";

            updated.customizations = [
              { key: "size", value: updated.selectedSize?.name || "" },
              {
                key: "dietary restrictions",
                value: updated.selectedDiet?.name || "None",
              },
              {
                key: "gift packaging",
                value: updated.giftPackaging ? "Yes" : "No",
              },
            ];
          }
          return null;
        };

        const product = getProduct();

        if (product) {
          updated.name = product.name;
          updated.displayName = product.displayName;
          updated.productId = product._id;

          if (!("image" in updatedFields)) {
            if (updated.type === "standardCake") {
              updated.image = updated.flavor?.image || "";
            } else {
              updated.image = product?.image || product?.images?.primary || "";
            }
          }

          const sizeInfo = product.sizes?.find((s) => s.name === sizeName);
          if (sizeInfo) {
            updated.unitPrice = sizeInfo.price;
          }
        }

        // Always recalculate total
        const qty = parseFloat(updated.quantity) || 0;
        const price = parseFloat(updated.unitPrice) || 0;
        updated.totalPrice = parseFloat((qty * price).toFixed(2));

        // START from item.customizations
        let baseCustomizations = [...(item.customizations || [])];

        // If we’re updating customizations explicitly (like from Add Customization)
        if ("customizations" in updatedFields) {
          baseCustomizations = [...updatedFields.customizations];
        }

        const keysToRemove = new Set([
          "size",
          "flavor",
          "dietary",
          "dietary restrictions",
          "giftPackaging",
          "gift packaging",
        ]);

        baseCustomizations = baseCustomizations.filter(
          (c) => !keysToRemove.has(c.key)
        );

        // Re-add normalized values
        if (updated.size?.name) {
          baseCustomizations.push({
            key: "size",
            value: updated.size.name,
          });
        }

        if (updated.flavor?.name) {
          baseCustomizations.push({
            key: "flavor",
            value: updated.flavor.displayName || updated.flavor.name,
          });
        }

        if (updated.selectedDiet?.name) {
          baseCustomizations.push({
            key: "dietary restrictions",
            value: updated.selectedDiet.name,
          });
        }

        if (updated.hasOwnProperty("giftPackaging")) {
          baseCustomizations.push({
            key: "gift packaging",
            value: updated.giftPackaging ? "Yes" : "No",
          });
        }

        updated.customizations = baseCustomizations;

        return updated;
      });

      return { ...prev, items: updatedItems };
    });
  };

  const removeItem = (id) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item._id !== id),
    }));
  };

  // Recalculate total
  const updatePaymentSummary = () => {
    const subtotal = parseFloat(formData.payment.manualAmount || 0);
    const tax = 0; // Placeholder for now
    const deliveryFee = formData.fulfillment.method === "delivery" ? 9.95 : 0;
    const total = parseFloat((subtotal + tax + deliveryFee).toFixed(2));

    setFormData((prev) => ({
      ...prev,
      payment: {
        ...prev.payment,
        subtotal,
        tax,
        deliveryFee,
        total,
      },
    }));
  };

  const findStandardCakeByFlavor = (flavor) =>
    formDataCache.current.standardCakes.find((c) =>
      c.flavors?.some((f) => f.name === flavor)
    );

  // const findCheesecakeByFlavor = (flavorName) =>
  //   formDataCache.current.cheesecakes.find(
  //     (c) =>
  //       (c.flavor || "").toLowerCase().trim() ===
  //       flavorName?.toLowerCase().trim()
  //   );

  const getFlavorByName = (name) =>
    formDataCache.current.flavors?.find((f) => f.name === name);

  const getSizeByName = (name) =>
    formDataCache.current.sizes?.find((s) => s.name === name);

  const getFlavorById = (id) =>
    formDataCache.current.flavors.find((f) => f._id?.toString() === id);

  const getSizeById = (id) =>
    formDataCache.current.sizes.find((s) => s._id?.toString() === id);

  const findGrazeBoxById = (id) =>
    formDataCache.current.grazeBoxes.find((g) => g._id === id);

  // Step-by-step validation
  const validateStep = (stepIndex) => {
    const errors = {};

    if (stepIndex === 0) {
      if (!formData.customer.firstName?.trim())
        errors.firstName = "First name is required.";
      if (!formData.customer.lastName?.trim())
        errors.lastName = "Last name is required.";
      if (!formData.customer.email?.trim()) {
        errors.email = "Email is required.";
      } else if (!isValidEmail(formData.customer.email)) {
        errors.email = "Invalid email format.";
      }

      if (!formData.customer.phone?.trim()) {
        errors.phone = "Phone number is required.";
      } else if (!isValidPhone(formData.customer.phone)) {
        errors.phone = "Invalid phone number.";
      }
    }

    if (stepIndex === 1) {
      if (!formData.fulfillment.date)
        errors.date = "Fulfillment date is required.";
      if (!formData.fulfillment.time?.trim())
        errors.time = "Fulfillment time is required.";
      if (!formData.fulfillment.location?.trim())
        errors.location = "Fulfillment location is required.";
    }

    if (stepIndex === 2) {
      if (!formData.items.length)
        errors.items = "At least one item is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Full form validation for final submit
  const validateAll = () => {
    const errors = {};

    if (!formData.customer.firstName?.trim())
      errors.firstName = "First name is required.";
    if (!formData.customer.lastName?.trim())
      errors.lastName = "Last name is required.";
    if (!formData.customer.email?.trim()) errors.email = "Email is required.";
    if (!formData.customer.phone?.trim())
      errors.phone = "Phone number is required.";

    if (!formData.fulfillment.date)
      errors.date = "Fulfillment date is required.";
    if (!formData.fulfillment.time?.trim())
      errors.time = "Fulfillment time is required.";
    if (!formData.fulfillment.location?.trim())
      errors.location = "Fulfillment location is required.";

    if (!formData.items.length) errors.items = "At least one item is required.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return {
    step,
    nextStep,
    prevStep,
    goToStep,
    formData,
    setFormData,
    formErrors,
    addItem,
    updateItem,
    removeItem,
    updatePaymentSummary,
    validateStep,
    validateAll,
    resetFormData,
  };
};

export default useAdminOrderFormData;
